/**
 * Helper functions for image alt text management
 * Ensures all images have locale-specific alt text for SEO and accessibility
 */

/**
 * Normalize partner name to match translation key
 * Maps displayed names to translation keys
 */
export function normalizePartnerName(name: string): string {
  const mapping: Record<string, string> = {
    'Lightspeed': 'lightspeed',
    'unTill': 'untill',
    'Bork': 'bork',
    'Oracle': 'oracle',
    'Deliverect': 'deliverect',
    'MplusKASSA': 'mpluskassa',
    'Trivec': 'trivec',
    'Gastrofix': 'gastrofix',
    'NuvoPOS': 'nuvopos',
    'Vectron': 'vectron',
    'Twelve': 'twelve',
    'NCR Aloha': 'ncr-aloha',
    'CashDesk': 'cashdesk',
    'Mr. Winston': 'mr-winston',
    'Hypersoft': 'hypersoft',
  };

  return mapping[name] || name.toLowerCase().replace(/\s+/g, '-');
}

/**
 * Get venue type translation key from image path
 * Maps image filenames to translation keys
 */
export function getVenueKeyFromImage(imagePath: string): string {
  const mapping: Record<string, string> = {
    '/images/venues/cafe.webp': 'cafes',
    '/images/venues/restaurant.webp': 'restaurants',
    '/images/venues/events.webp': 'events',
    '/images/venues/beach.webp': 'beach',
    '/images/venues/parks.webp': 'parks',
    '/images/venues/hotels.webp': 'hotels',
    '/images/venues/bowling.webp': 'bowling',
    '/images/venues/golf.webp': 'golf',
    '/images/venues/zoo.webp': 'zoo',
    '/images/venues/airports.webp': 'airports',
    '/images/venues/hospitals.webp': 'hospitals',
    '/images/venues/theaters.webp': 'theaters',
    '/images/venues/amusement.webp': 'amusement',
    '/images/venues/stadium.webp': 'stadium',
    '/images/venues/fastfood.webp': 'fastfood',
    '/images/venues/foodhall.webp': 'foodhall',
    '/images/venues/buffet.webp': 'buffet',
    '/images/venues/lunchroom.webp': 'lunchroom',
    '/images/venues/bakery.webp': 'bakery',
    '/images/venues/foodtruck.webp': 'foodtruck',
    '/images/venues/catering.webp': 'catering',
    '/images/venues/cruise.webp': 'cruise',
    '/images/venues/casino.webp': 'casino',
    '/images/venues/spa.webp': 'spa',
    '/images/venues/museum.webp': 'museum',
    '/images/venues/congress.webp': 'congress',
    '/images/venues/concert.webp': 'concert',
    '/images/venues/ferry.webp': 'ferry',
    '/images/venues/expo.webp': 'expo',
    '/images/venues/canteen.webp': 'canteen',
    '/images/venues/university.webp': 'university',
    '/images/venues/icecream.webp': 'icecream',
  };

  return mapping[imagePath] || '';
}

/**
 * Get product type translation key
 */
export function getProductTypeKey(productType: 'kiosk' | 'tablet' | 'qr' | 'webshop'): string {
  return productType;
}

/**
 * Get blog image alt text
 * Provides locale-specific alt text for blog images
 */
export function getBlogImageAlt(slug: string, imageType: 'hero' | 'content', locale: 'en' | 'nl' | 'de'): string {
  const blogImageAlts: Record<string, Record<string, Record<string, string>>> = {
    'the-new-hospitality-industry': {
      hero: {
        en: 'People celebrating with drinks in hospitality venue - representing the new digital hospitality industry',
        nl: 'Mensen vieren met drankjes in horecagelegenheid - de nieuwe digitale horeca industrie',
        de: 'Menschen feiern mit Getränken in Gastronomiebetrieb - die neue digitale Gastronomie-Industrie',
      },
      content: {
        en: 'Person managing digital ordering platform on laptop - Jamezz hospitality software',
        nl: 'Persoon beheert digitaal bestelplatform op laptop - Jamezz horeca software',
        de: 'Person verwaltet digitale Bestellplattform auf Laptop - Jamezz Gastronomie-Software',
      },
    },
    'the-psychology-behind-upselling': {
      hero: {
        en: 'Happy customers in restaurant enjoying experience - psychology behind upselling strategies',
        nl: 'Gelukkige klanten in restaurant genieten van ervaring - psychologie achter upselling strategieën',
        de: 'Glückliche Kunden im Restaurant genießen Erlebnis - Psychologie hinter Upselling-Strategien',
      },
      content: {
        en: 'Digital ordering system interface with smart upselling features - AI-powered restaurant technology',
        nl: 'Digitaal bestelsysteem interface met slimme upselling functies - AI-aangedreven restaurant technologie',
        de: 'Digitales Bestellsystem-Interface mit intelligenten Upselling-Funktionen - KI-gesteuerte Restaurant-Technologie',
      },
    },
    'self-order-kiosks-for-hospitality': {
      hero: {
        en: 'Modern self-service kiosk in restaurant - digital ordering technology for hospitality',
        nl: 'Moderne selfservice kiosk in restaurant - digitale bestel technologie voor horeca',
        de: 'Moderner Self-Service Kiosk im Restaurant - digitale Bestell-Technologie für Gastronomie',
      },
      content: {
        en: 'Smart upselling interface on digital kiosk screen - restaurant revenue optimization',
        nl: 'Slimme upselling interface op digitaal kiosk scherm - restaurant omzet optimalisatie',
        de: 'Intelligente Upselling-Oberfläche auf digitalem Kiosk-Bildschirm - Restaurant-Umsatzoptimierung',
      },
    },
    'golf-clubs-digital-ordering': {
      hero: {
        en: 'Female golfer on golf course using digital ordering technology - modern golf club hospitality',
        nl: 'Vrouwelijke golfer op golfbaan die digitale bestel technologie gebruikt - moderne golfclub horeca',
        de: 'Golferin auf Golfplatz mit digitaler Bestell-Technologie - moderne Golfclub-Gastronomie',
      },
      content: {
        en: 'Golf club digital ordering solution with QR code - seamless F&B service on the golf course',
        nl: 'Golfclub digitale bestel oplossing met QR-code - naadloze F&B service op de golfbaan',
        de: 'Golfclub digitale Bestelllösung mit QR-Code - nahtloser F&B-Service auf dem Golfplatz',
      },
    },
    'qr-ordering-restaurant-guide': {
      hero: {
        en: 'Restaurant staff reviewing QR code ordering system - implementing digital ordering in hospitality',
        nl: 'Restaurant personeel bekijkt QR-code bestelsysteem - digitaal bestellen implementeren in horeca',
        de: 'Restaurantpersonal überprüft QR-Code-Bestellsystem - digitale Bestellung in der Gastronomie implementieren',
      },
      content: {
        en: 'Smart restaurateur preparing business for QR ordering - hospitality digital transformation guide',
        nl: 'Slimme restaurateur bereidt bedrijf voor op QR bestellen - horeca digitale transformatie gids',
        de: 'Smarter Gastronom bereitet Geschäft auf QR-Bestellung vor - Gastronomie digitale Transformationsleitfaden',
      },
    },
    'bakery-ordering-software': {
      hero: {
        en: 'Bakker Bart bakery with modern self-service kiosk - digital ordering technology for bakeries',
        nl: 'Bakker Bart bakkerij met moderne selfservice kiosk - digitale bestel technologie voor bakkerijen',
        de: 'Bakker Bart Bäckerei mit modernem Self-Service-Kiosk - digitale Bestell-Technologie für Bäckereien',
      },
      content: {
        en: 'Customers using bakery ordering kiosk - streamlining bakery operations with digital technology',
        nl: 'Klanten gebruiken bakkerij bestel kiosk - bakkerij operaties stroomlijnen met digitale technologie',
        de: 'Kunden nutzen Bäckerei-Bestellkiosk - Bäckerei-Abläufe mit digitaler Technologie optimieren',
      },
    },
    'choosing-ordering-solution': {
      hero: {
        en: 'Restaurant owner choosing digital ordering solution - comparing QR codes, kiosks, and tablets for hospitality',
        nl: 'Restaurant eigenaar kiest digitale besteloplossing - QR-codes, kiosken en tablets vergelijken voor horeca',
        de: 'Restaurantbesitzer wählt digitale Bestelllösung - QR-Codes, Kioske und Tablets für Gastronomie vergleichen',
      },
      content: {
        en: 'Guide to selecting the right ordering technology - hospitality digital transformation decision making',
        nl: 'Gids voor het selecteren van de juiste besteltechnologie - horeca digitale transformatie besluitvorming',
        de: 'Leitfaden zur Auswahl der richtigen Bestelltechnologie - Gastronomie digitale Transformationsentscheidung',
      },
    },
    'hotel-digital-ordering': {
      hero: {
        en: 'Modern hotel with digital ordering technology - self-service kiosk and QR code room service innovation',
        nl: 'Modern hotel met digitale besteltechnologie - selfservice kiosk en QR-code roomservice innovatie',
        de: 'Modernes Hotel mit digitaler Bestelltechnologie - Self-Service-Kiosk und QR-Code-Zimmerservice-Innovation',
      },
      content: {
        en: 'Hotel guests using digital ordering for room service and terrace - seamless hospitality technology experience',
        nl: 'Hotelgasten gebruiken digitaal bestellen voor roomservice en terras - naadloze horeca technologie ervaring',
        de: 'Hotelgäste nutzen digitale Bestellung für Zimmerservice und Terrasse - nahtloses Gastronomie-Technologie-Erlebnis',
      },
    },
    'restaurant-kpi-guide': {
      hero: {
        en: 'Restaurant owner analyzing KPI data on laptop - data-driven decision making for modern restaurants',
        nl: 'Restaurant eigenaar analyseert KPI data op laptop - datagedreven besluitvorming voor moderne restaurants',
        de: 'Restaurantbesitzer analysiert KPI-Daten auf Laptop - datengesteuerte Entscheidungsfindung für moderne Restaurants',
      },
      content: {
        en: 'Digital ordering metrics and restaurant performance indicators - optimizing profits with business intelligence',
        nl: 'Digitale bestel metrics en restaurant prestatie indicatoren - winsten optimaliseren met business intelligence',
        de: 'Digitale Bestellmetriken und Restaurant-Leistungsindikatoren - Gewinne optimieren mit Business Intelligence',
      },
    },
    'snackbar-digital-ordering': {
      hero: {
        en: 'Modern snackbar with self-service kiosk - digital ordering revolution in cafeteria and snackbar industry',
        nl: 'Moderne snackbar met selfservice kiosk - digitale bestel revolutie in cafetaria en snackbar industrie',
        de: 'Moderne Snackbar mit Self-Service-Kiosk - digitale Bestellrevolution in Cafeteria- und Snackbar-Branche',
      },
      content: {
        en: 'Customers using digital ordering in cafeteria - boosting efficiency and sales with modern technology',
        nl: 'Klanten gebruiken digitaal bestellen in cafetaria - efficiëntie en verkoop boosten met moderne technologie',
        de: 'Kunden nutzen digitale Bestellung in Cafeteria - Effizienz und Verkäufe mit moderner Technologie steigern',
      },
    },
    'all-you-can-eat-ordering-system': {
      hero: {
        en: 'All-you-can-eat restaurant guests enjoying food with digital ordering - modern AYCE dining experience',
        nl: 'All-you-can-eat restaurant gasten genieten van eten met digitaal bestellen - moderne AYCE eetervaring',
        de: 'All-you-can-eat Restaurant-Gäste genießen Essen mit digitaler Bestellung - modernes AYCE-Erlebnis',
      },
      content: {
        en: 'Smart tablet ordering system for all-you-can-eat restaurant - session timers and round counters for efficiency',
        nl: 'Slim tablet bestelsysteem voor all-you-can-eat restaurant - sessie timers en ronde tellers voor efficiëntie',
        de: 'Intelligentes Tablet-Bestellsystem für All-you-can-eat Restaurant - Session-Timer und Runden-Zähler für Effizienz',
      },
    },
    'bowling-alley-digital-ordering': {
      hero: {
        en: 'Modern bowling alley with digital ordering technology - QR codes, tablets and kiosks for leisure hospitality',
        nl: 'Moderne bowlingbaan met digitale besteltechnologie - QR-codes, tablets en kiosken voor leisure horeca',
        de: 'Moderne Bowlingbahn mit digitaler Bestelltechnologie - QR-Codes, Tablets und Kioske für Freizeitgastronomie',
      },
      content: {
        en: 'Bowling guests using digital ordering from their lane - faster service and higher revenue with smart technology',
        nl: 'Bowling gasten gebruiken digitaal bestellen vanaf hun baan - snellere service en hogere omzet met slimme technologie',
        de: 'Bowling-Gäste nutzen digitale Bestellung von ihrer Bahn - schnellerer Service und höherer Umsatz mit intelligenter Technologie',
      },
    },
  };

  return blogImageAlts[slug]?.[imageType]?.[locale] || '';
}

/**
 * Get blog post title for image alt (when specific alt text is not defined)
 * Fallback for dynamic content
 */
export function getBlogFallbackAlt(title: string, locale: 'en' | 'nl' | 'de'): string {
  const prefix = {
    en: 'Blog post:',
    nl: 'Blog artikel:',
    de: 'Blogartikel:',
  };
  
  return `${prefix[locale]} ${title}`;
}

